import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { modelCategories, modelInfo, trainConfig } from "@/mocks/models";
import { useModelArticleData } from "@/hooks/useModelArticleData";
import ArticlePickerModal from "@/pages/model/settings/components/ArticlePickerModal";
import TrainDataModal from "@/pages/model/settings/components/TrainDataModal";

interface ProgressState {
  running: boolean;
  percent: number;
}

interface PickerState {
  open: boolean;
  categoryId: number | null;
  categoryName: string;
  split: "train" | "test";
}

const emptyPicker: PickerState = {
  open: false,
  categoryId: null,
  categoryName: "",
  split: "train",
};

export default function ModelSettings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { categories, loading, error, reload, clearSplit } = useModelArticleData();

  const [name, setName] = useState(modelInfo.name);
  const [editingName, setEditingName] = useState(false);
  const [types, setTypes] = useState<string[]>(modelCategories);
  const [editingType, setEditingType] = useState(false);
  const [newType, setNewType] = useState("");

  const [epoch, setEpoch] = useState(String(trainConfig.epoch));
  const [learningRate, setLearningRate] = useState(String(trainConfig.learningRate));

  const [trainProgress, setTrainProgress] = useState<ProgressState>({ running: false, percent: 0 });
  const [testProgress, setTestProgress] = useState<ProgressState>({ running: false, percent: 0 });
  const [trainDone, setTrainDone] = useState(true);
  const [testDone, setTestDone] = useState(true);

  const [picker, setPicker] = useState<PickerState>(emptyPicker);
  const [trainDataOpen, setTrainDataOpen] = useState(false);

  const runProgress = (
    setter: (value: ProgressState) => void,
    setDone: (value: boolean) => void
  ) => {
    setDone(false);
    setter({ running: true, percent: 0 });
    let current = 0;
    const timer = window.setInterval(() => {
      current += Math.floor(Math.random() * 12) + 5;
      if (current >= 100) {
        window.clearInterval(timer);
        setter({ running: false, percent: 100 });
        setDone(true);
      } else {
        setter({ running: true, percent: current });
      }
    }, 220);
  };

  const addType = () => {
    const value = newType.trim();
    if (value && !types.includes(value)) {
      setTypes((prev) => [...prev, value]);
    }
    setNewType("");
  };

  const openPicker = (categoryId: number, categoryName: string, split: "train" | "test") => {
    setPicker({ open: true, categoryId, categoryName, split });
  };

  const handleClear = async (split: "train" | "test") => {
    try {
      await clearSplit(split);
    } catch {
      // 忽略，界面保持原状
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-8 md:px-8">
      <div className="mb-6 flex items-center gap-2 border-b border-background-200 pb-3">
        <button type="button" className="cursor-pointer text-base font-semibold text-primary-600">
          模型设置
        </button>
        <span className="text-xs text-foreground-400">/ {id}</span>
      </div>

      <section className="mb-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground-950">模型信息</h2>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex cursor-pointer items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
          >
            <i className="ri-delete-bin-6-line" />
            删除模型
          </button>
        </div>

        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[80px_1fr_auto] sm:items-center">
          <span className="text-sm text-foreground-600">名称</span>
          <div>
            {editingName ? (
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="h-9 w-full max-w-xs rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            ) : (
              <span className="text-sm font-medium text-foreground-900">{name}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditingName((prev) => !prev)}
            className="cursor-pointer text-sm text-primary-600 hover:text-primary-700"
          >
            {editingName ? "保存" : "修改"}
          </button>

          <span className="text-sm text-foreground-600">类型</span>
          <div className="flex flex-wrap items-center gap-2">
            {types.map((type) => (
              <span
                key={type}
                className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
              >
                {type}
              </span>
            ))}
            {editingType ? (
              <span className="flex items-center gap-1">
                <input
                  type="text"
                  value={newType}
                  onChange={(event) => setNewType(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") addType();
                  }}
                  placeholder="新增类型"
                  className="h-7 w-24 rounded-md border border-background-300 bg-background-50 px-2 text-xs outline-none focus:border-primary-400"
                />
                <button
                  type="button"
                  onClick={addType}
                  className="cursor-pointer text-xs text-primary-600"
                >
                  添加
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setEditingType(true)}
                className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-add-line" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditingType((prev) => !prev)}
            className="cursor-pointer text-sm text-primary-600 hover:text-primary-700"
          >
            {editingType ? "完成" : "修改"}
          </button>
        </div>
      </section>

      <section className="mb-6 rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between border-b border-background-100 px-5 py-3.5">
          <h2 className="text-sm font-semibold text-foreground-950">训练模型</h2>
          <button
            type="button"
            onClick={() => handleClear("train")}
            className="cursor-pointer text-xs text-primary-600 hover:text-primary-700"
          >
            清空所有选择
          </button>
        </div>
        <div className="px-5 py-5">
          <p className="mb-4 rounded-md bg-background-100 px-3 py-2.5 text-xs leading-relaxed text-foreground-600">
            从数据库中为各分类选择训练文章，数量尽量大于 1200 篇，各类型文章数量尽量均衡，相差不大于 120 篇。
          </p>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-lg" />
              正在从数据库加载分类与文章...
            </div>
          ) : error ? (
            <div className="flex items-center justify-between rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-600">
              <span>{error}</span>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer whitespace-nowrap text-primary-600 hover:text-primary-700"
              >
                重试
              </button>
            </div>
          ) : (
            <div className="divide-y divide-background-100">
              {categories.map((category) => (
                <div key={category.id} className="flex items-center justify-between py-3">
                  <span className="text-sm text-foreground-900">
                    {category.name}（已选择
                    <span className="font-medium text-primary-600">{category.trainCount}</span>篇 / 共
                    {category.totalCount}篇）
                  </span>
                  <button
                    type="button"
                    onClick={() => openPicker(category.id, category.name, "train")}
                    className="cursor-pointer text-sm text-primary-600 hover:text-primary-700"
                  >
                    选择
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-foreground-700">
              遍历次数
              <input
                type="number"
                value={epoch}
                onChange={(event) => setEpoch(event.target.value)}
                className="h-9 w-24 rounded-md border border-background-300 bg-background-50 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              次
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground-700">
              学习率
              <input
                type="text"
                value={learningRate}
                onChange={(event) => setLearningRate(event.target.value)}
                className="h-9 w-24 rounded-md border border-background-300 bg-background-50 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </label>
          </div>

          {trainProgress.running || trainProgress.percent === 100 ? (
            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between text-xs text-foreground-600">
                <span>{trainProgress.running ? "模型训练中..." : "训练完成"}</span>
                <span>{trainProgress.percent}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-background-200">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all duration-200"
                  style={{ width: `${trainProgress.percent}%` }}
                />
              </div>
            </div>
          ) : null}

          <div className="mt-5 flex flex-col items-start gap-3 border-t border-background-100 pt-5 sm:flex-row sm:items-center">
            <button
              type="button"
              disabled={trainProgress.running}
              onClick={() => runProgress(setTrainProgress, setTrainDone)}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              开始训练
            </button>
            {trainDone && !trainProgress.running ? (
              <span className="text-xs text-foreground-500">
                上次训练时间 2026/10/13，已完成，
                <button
                  type="button"
                  onClick={() => setTrainDataOpen(true)}
                  className="cursor-pointer text-primary-600 hover:text-primary-700"
                >
                  查看训练数据
                </button>
              </span>
            ) : null}
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between border-b border-background-100 px-5 py-3.5">
          <h2 className="text-sm font-semibold text-foreground-950">测试模型</h2>
          <button
            type="button"
            onClick={() => handleClear("test")}
            className="cursor-pointer text-xs text-primary-600 hover:text-primary-700"
          >
            清空所有选择
          </button>
        </div>
        <div className="px-5 py-5">
          <p className="mb-4 rounded-md bg-background-100 px-3 py-2.5 text-xs leading-relaxed text-foreground-600">
            从数据库中为各分类选择测试文章，数量约等于 400 篇，各类型文章数量尽量均衡，相差不大于 40 篇。
          </p>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-lg" />
              正在从数据库加载分类与文章...
            </div>
          ) : error ? (
            <div className="flex items-center justify-between rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-600">
              <span>{error}</span>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer whitespace-nowrap text-primary-600 hover:text-primary-700"
              >
                重试
              </button>
            </div>
          ) : (
            <div className="divide-y divide-background-100">
              {categories.map((category) => (
                <div key={category.id} className="flex items-center justify-between py-3">
                  <span className="text-sm text-foreground-900">
                    {category.name}（已选择
                    <span className="font-medium text-primary-600">{category.testCount}</span>篇 / 共
                    {category.totalCount}篇）
                  </span>
                  <button
                    type="button"
                    onClick={() => openPicker(category.id, category.name, "test")}
                    className="cursor-pointer text-sm text-primary-600 hover:text-primary-700"
                  >
                    选择
                  </button>
                </div>
              ))}
            </div>
          )}

          {testProgress.running || testProgress.percent === 100 ? (
            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between text-xs text-foreground-600">
                <span>{testProgress.running ? "模型测试中..." : "测试完成"}</span>
                <span>{testProgress.percent}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-background-200">
                <div
                  className="h-full rounded-full bg-accent-500 transition-all duration-200"
                  style={{ width: `${testProgress.percent}%` }}
                />
              </div>
            </div>
          ) : null}

          <div className="mt-5 flex flex-col items-start gap-3 border-t border-background-100 pt-5 sm:flex-row sm:items-center">
            <button
              type="button"
              disabled={testProgress.running}
              onClick={() => runProgress(setTestProgress, setTestDone)}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              开始测试
            </button>
            {testDone && !testProgress.running ? (
              <span className="text-xs text-foreground-500">
                上次测试时间 2026/10/13，已完成，
                <button
                  type="button"
                  onClick={() => navigate("/results")}
                  className="cursor-pointer text-primary-600 hover:text-primary-700"
                >
                  查看测试结果
                </button>
              </span>
            ) : null}
          </div>
        </div>
      </section>

      <ArticlePickerModal
        open={picker.open}
        categoryId={picker.categoryId}
        categoryName={picker.categoryName}
        split={picker.split}
        onClose={() => setPicker(emptyPicker)}
        onSaved={reload}
      />

      <TrainDataModal
        open={trainDataOpen}
        modelName={name}
        epoch={epoch}
        learningRate={learningRate}
        onClose={() => setTrainDataOpen(false)}
      />
    </div>
  );
}